import angular from 'angular';
import reportDialogTemplate from './ReportsDialog.html';

/****************************************************************************
 * Service: ReportsDialog */
/**
 * @ngdoc     service
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.service:ReportsDialog
 * @description
 * Modal dialog to generate reports for events.
 *****************************************************************************/
/* @ngInject */ export function ReportsDialogService($uibModal) {
  /***************************************************************************
   * show */
  /**
   * @ngdoc     function
   * @name      show
   * @module    csm.books.eventsAdmin
   * @methodOf  csm.books.eventsAdmin.service:ReportsDialog
   * @return   {Promise}    Resolves upon close.
   * @description
   * Display the modal dialog.
   ***************************************************************************/
  this.show = function (events) {
    return $uibModal.open({
      animation: true,
      backdrop: 'static',
      component: 'reportsDialog',
      size: 'md',
      resolve: {
        events: function () {
          return events;
        }
      }
    }).result;
  };
}

/*****************************************************************************
 * Component: reportsDialog */
/**
 * @ngdoc     component
 * @module    csm.books.eventsAdmin
 * @name      csm.books.eventsAdmin.component:reportsDialog
 * @param  {Object} modalInstance $uibModal instance from $uibModal.open method
 * @param  {Object} resolve       Resolve object passed from $uibModal.open method
 * @description
 * Display dialog to generate reports for events.
 *****************************************************************************/
export const reportsDialogComponent = {
  template: reportDialogTemplate,
  bindings: { modalInstance: '<', resolve: '<' },
  controller: Controller
}

/*****************************************************************************
 * Controller: Controller */
/**
 * @ngdoc     controller
 * @module    csm.books.eventsAdmin
 * @methodof  csm.books.eventsAdmin.reportsDialog
 * @description
 * Controller for the reportsDialog component.
 *****************************************************************************/
/* @ngInject */ function Controller($injector, $scope) {
  var $ctrl = this;
  $ctrl.$onInit = $injector.invoke($onInit, null, {
    $ctrl: $ctrl,
    $scope: $scope
  });
  $ctrl.generateReport = $injector.invoke(generateReport, null, {
    $ctrl: $ctrl
  });
  $ctrl.individualLabel = $injector.invoke(individualLabel, null, {
    $ctrl: $ctrl
  });
  $ctrl.validate = $injector.invoke(validate, null, { $ctrl: $ctrl });
}

/*************************************************************************
 * generateReport */
/**
 * Builds the report in a new window.
 *************************************************************************/
/* @ngInject */ function generateReport($ctrl, Alert) {
  return function () {
    /*-----------------------------------------------------------*/
    /* Verify a report can be generated with the current inputs. */
    /*-----------------------------------------------------------*/
    if (!$ctrl.validate()) Alert.error('Invalid selection');

    /*-------------------------*/
    /* Event Report requested. */
    /*-------------------------*/
    if ($ctrl.report.reportType === $ctrl.reportTypes.EVENT) {
      var reportURL =
        $ctrl.report.typeOption === $ctrl.rtOpts.DETAIL
          ? 'EventDetailReport.aspx'
          : $ctrl.report.typeOption === $ctrl.rtOpts.SUMMARY
            ? 'EventSummaryReport.aspx'
            : '';

      /*------------------------------------------------*/
      /* Build request payload and call report handler. */
      /*------------------------------------------------*/
      var payload = {
        eventID: $ctrl.report.eventID,
        reportFormat: $ctrl.report.format,
        typeOption: $ctrl.report.typeOption
      };
      JSON.postTo(payload, reportURL, 'request', '_blank');
    } else if ($ctrl.report.reportType === $ctrl.reportTypes.ATTENDEE) {
      /*----------------------------*/
      /* Attendee Report requested. */
      /*----------------------------*/
      var reportURL =
        $ctrl.report.typeOption === $ctrl.rtOpts.DETAIL
          ? 'AttendeeDetailReport.aspx'
          : $ctrl.report.typeOption === $ctrl.rtOpts.NASUMMARY
            ? 'NonAttendeeSummaryReport.aspx'
            : '';

      /*--------------------------------------------------*/
      /* Detailed attendee report requested, build the    */
      /* request payload with grade or attendee provided. */
      /*--------------------------------------------------*/
      if ($ctrl.report.typeOption === $ctrl.rtOpts.DETAIL) {
        var reportURL = 'AttendeeDetailReport.aspx';
        var payload = {
          eventID: $ctrl.report.eventID,
          reportFormat: $ctrl.report.format,
          typeOption: $ctrl.rtOpts.DETAIL
        };

        /*-------------------------------------------*/
        /* Search filter, by grade or by individual. */
        /*-------------------------------------------*/
        if ($ctrl.report.detailType === $ctrl.detailTypes.GRADE) {
          payload.detailType = $ctrl.detailTypes.GRADE;
          payload.grades = $ctrl.report.selectedGrades.join(',');
        } else if ($ctrl.report.detailType === $ctrl.detailTypes.INDIVIDUAL) {
          payload.detailType = $ctrl.detailTypes.INDIVIDUAL;
          payload.attendeeID = $ctrl.report.individual;
        }
      } else if ($ctrl.report.typeOption === $ctrl.rtOpts.NASUMMARY) {
        /*------------------------------*/
        /* Non-Attendee Summary Report. */
        /*------------------------------*/
        var reportURL = 'NonAttendeeSummaryReport.aspx';
        var payload = {
          eventID: $ctrl.report.eventID,
          reportFormat: $ctrl.report.format,
          typeOption: $ctrl.rtOpts.NASUMMARY
        };
      }
      JSON.postTo(payload, reportURL, 'request', '_blank');
    }
  };
}

/*****************************************************************************
 * individualLabel */
/**
 * @ngdoc    function
 * @module   csm.books.eventsAdmin
 * @methodOf csm.books.eventsAdmin.reportsDialog.controller:Controller
 * @description
 * Label function for the `Individuals` lazyTypeahead input. Returns a string
 * with the student name and student ID in parentheses.
 *****************************************************************************/
/* @ngInject */ function individualLabel($ctrl) {
  return function (attendee) {
    if (!attendee) return;

    return (
      attendee.lastName +
      ', ' +
      attendee.firstName +
      (attendee.id ? ' (' + attendee.studentID + ')' : '')
    );
  };
}

/*****************************************************************************
 * $onInit */
/**
 * @ngdoc    function
 * @module   csm.books.eventsAdmin
 * @methodOf csm.books.eventsAdmin.reportsDialog.controller:Controller
 * @description
 * Called by angular when the component is ready. This will set up some
 * properties from the bound values.
 *****************************************************************************/
/* @ngInject */ function $onInit($ctrl, $scope, BusyScreen, Events) {
  return function () {
    /** Events List.   */ $ctrl.eventsList; // = $ctrl.resolve.events;
    /** Report Object. */ $ctrl.report = { reportType: null };
    /** Grades List.   */ $ctrl.gradesDp = [];

    /*----------------------------------------*/
    /* Dataproviders for radio button groups. */
    /*----------------------------------------*/
    /** Report Types.        */ $ctrl.reportTypes = {
      EVENT: 'E',
      ATTENDEE: 'A'
    };
    /** Report Type Options. */ $ctrl.rtOpts = {
      SUMMARY: 'S',
      DETAIL: 'D',
      NASUMMARY: 'NAS'
    };
    /** Detail Options.      */ $ctrl.detailTypes = {
      GRADE: 'G',
      INDIVIDUAL: 'I'
    };

    /*---------------------------------------*/
    /* Watch for changes to the report type. */
    /*---------------------------------------*/
    $scope.$watch('$ctrl.report.reportType', function (newVal, oldVal) {
      /*-------------------------------------*/
      /* No event type selection made, exit. */
      /*-------------------------------------*/
      if (!newVal || newVal === oldVal) return;

      /*--------------------------------------*/
      /* Event type changed, preserve the new */
      /* type and clear all other options.    */
      /*--------------------------------------*/
      if (!!newVal && !!oldVal && newVal !== oldVal)
        $ctrl.report = { reportType: newVal };
    });

    /*--------------------------------------------------*/
    /* Populate the events dataprovider when the report */
    /* type option (summary/detail) changes. Exclude    */
    /* open events for the non-attendee summary option. */
    /*--------------------------------------------------*/
    $scope.$watch('$ctrl.report.typeOption', function (newVal, oldVal) {
      var useTicketedEvents = newVal === $ctrl.rtOpts.NASUMMARY;
      $ctrl.eventsList = $ctrl.resolve.events.filter(function (e) {
        return useTicketedEvents ? !e.openEvent : true;
      });
    });

    /*----------------------------------------------------*/
    /* Watch for changes to the report detail type.       */
    /* Clear grade selection and individual when changed. */
    /*----------------------------------------------------*/
    $scope.$watch('$ctrl.report.detailType', function (newVal, oldVal) {
      /*---------------------------*/
      /* No selection made, abort. */
      /*---------------------------*/
      if (!newVal || newVal === oldVal) return;

      /*----------------------------------------*/
      /* Clear individual and grade selections. */
      /*----------------------------------------*/
      $ctrl.report.selectedGrades = null;
      $ctrl.report.individual = null;
    });

    /*--------------------------------------------*/
    /* When event selection for detailed reports, */
    /* load the individuals dataprovider.         */
    /*--------------------------------------------*/
    $scope.$watch('$ctrl.report.eventID', function (newID, oldID) {
      /*--------------------------*/
      /* No event selected, exit. */
      /*--------------------------*/
      if (!newID || newID === oldID) return;

      /*-----------------------------------------------*/
      /* Event report type, no need to load attendees. */
      /*-----------------------------------------------*/
      if ($ctrl.report.reportType === $ctrl.reportTypes.EVENT) return;

      /*---------------------------------------------------------*/
      /* Load the list of attendees for the typeahead            */
      /* dataprovider, clear the attendee and grades selections. */
      /*---------------------------------------------------------*/
      BusyScreen.promise(
        Events.getAttendees($ctrl.report.eventID).then(function (attendees) {
          $ctrl.report.detailType = null;
          $ctrl.dpIndividuals = attendees;
          $ctrl.report.individual = null;
          $ctrl.report.selectedGrades = [];

          /*---------------------------------------------------*/
          /* For Open Events, populate the grades dataprovider */
          /* with only those grades allowed for the event.     */
          /*---------------------------------------------------*/
          $ctrl.gradesDp = [];
          var match = $ctrl.eventsList.find(function (evt) {
            return evt.id === newID;
          });
          if (match.openEvent)
            angular.forEach(match.gradesList.split(','), function (g) {
              $ctrl.gradesDp.push({ label: g });
            });

          /*------------------------------------------------*/
          /* Used to hide the grade filter ticketed events. */
          /*------------------------------------------------*/
          $ctrl.useGrades = match.openEvent;
        })
      );
    });

    /*----------------------------------------*/
    /* Reference to the modal dismiss method. */
    /*----------------------------------------*/
    $ctrl.dismiss = $ctrl.modalInstance.dismiss;
  };
}

/*****************************************************************************
 * validate */
/**
 * @ngdoc    function
 * @module   csm.books.eventsAdmin
 * @methodOf csm.books.eventsAdmin.reportsDialog.controller:Controller
 * @description
 * Verifies a report can be generated with the options selected, and all required
 * options and sub-options are selected. Returns false, if not.
 *****************************************************************************/
/* @ngInject */ function validate($ctrl) {
  return function () {
    /*-------------------------------*/
    /* Report Type must be selected. */
    /*-------------------------------*/
    if (
      $ctrl.report.reportType !== $ctrl.reportTypes.EVENT &&
      $ctrl.report.reportType !== $ctrl.reportTypes.ATTENDEE
    )
      return false;

    /*-------------------------------*/
    /* Type Option must be selected. */
    /*-------------------------------*/
    if (
      $ctrl.report.typeOption !== $ctrl.rtOpts.SUMMARY &&
      $ctrl.report.typeOption !== $ctrl.rtOpts.DETAIL &&
      $ctrl.report.typeOption !== $ctrl.rtOpts.NASUMMARY
    )
      return false;

    /*-----------------------------------------*/
    /* All report options require an event ID. */
    /*-----------------------------------------*/
    if (!$ctrl.report.eventID) return false;

    /*-----------------------------------------*/
    /* Detailed attendee reports need grades   */
    /* (for open events) or attendee selected. */
    /*-----------------------------------------*/
    if ($ctrl.report.reportType === $ctrl.reportTypes.ATTENDEE)
      if ($ctrl.report.typeOption === $ctrl.rtOpts.DETAIL)
        if ($ctrl.report.detailType === $ctrl.detailTypes.GRADE) {
          if (
            $ctrl.useGrades &&
            (!$ctrl.report.selectedGrades ||
              $ctrl.report.selectedGrades.length === 0)
          )
            return false;
        } else if ($ctrl.report.detailType === $ctrl.detailTypes.INDIVIDUAL) {
          if (!$ctrl.report.individual) return false;
        } else return false;

    /*--------------------------*/
    /* Report formart required. */
    /*--------------------------*/
    if (!$ctrl.report.format) return false;

    /*--------------------------*/
    /* Report can be generated. */
    /*--------------------------*/
    return true;
  };
}
